import type { CustomerProductsRequestType, PostPaymentOrderRequest } from '@dtp/membership-service-types';
import { IdpCustomerFormType } from '@appTypes/idpCustomerFormType';
import { InternationalDrivingPermitFormType, PickupType } from '@appTypes/internationalDrivingPermitFormType';
import { formatDateOfBirth } from '@shared/lib/payloadTransformers';
import { formatPhoneNumber } from '../../../lib/phoneValidation';

export const createOrderPayload = (
  customerData: Partial<IdpCustomerFormType>,
  productData: Partial<InternationalDrivingPermitFormType>,
  returnUrl: string,
  cancelUrl: string,
  orchestrationId: string,
  campaignCode?: string,
  customerId?: string,
): PostPaymentOrderRequest => {
  const pickupType = productData.pickupAtOffice ? PickupType.Office : PickupType.Mail;
  const hasSelectedMembership = !!productData.membership?.productId;
  const customer: CustomerProductsRequestType = {
    profile: {
      isMainHouseholdMember: true,
      firstName: customerData.firstName,
      lastName: customerData.lastName,
      email: customerData.eMail,
      phoneNumber: customerData.phoneNumber?.number
        ? formatPhoneNumber({
            countryCode: customerData.phoneNumber.countryCode ?? '47',
            number: customerData.phoneNumber.number,
          })
        : null,
      ...(customerData.birthDate?.year && customerData.birthDate?.month && customerData.birthDate?.day
        ? { dateOfBirth: formatDateOfBirth(customerData.birthDate) }
        : {}),
      gender: customerData.gender ? customerData.gender : '',
      address: customerData.address,
      postalCode: customerData.zip,
      cityOfBirth: customerData.cityOfBirth,
      countryOfBirth: customerData.countryOfBirth,
    },
    products: [
      ...(productData.membership?.productId
        ? [
            {
              productId: productData.membership.productId.toString(),
              isHouseholdProduct: true,
            },
          ]
        : []),
      ...(productData.product?.productId
        ? [
            {
              productId: productData.product.productId.toString(),
              isHouseholdProduct: false,
              props: {
                IfkPickupType: pickupType,
              },
            },
          ]
        : []),
    ],
  };

  if (hasSelectedMembership) {
    customer.consents =
      customerData.consents
        ?.filter((consent) => consent?.id)
        ?.map((consent) => ({
          consentNumber: consent.id as string,
          consented: consent.consented || false,
          consentSource: 'naf.no',
        })) ?? [];
    customer.useConsentServiceV3 = true;
  }

  if (customerId) {
    customer.customerId = customerId;
  }

  const result: PostPaymentOrderRequest = {
    customerProductsRequests: [customer],
    salesAgent: 'innmelding@naf.no',
    salesChannel: 'web',
    salesLocationNumber: '86646379',
    returnUrl,
    cancelUrl,
    orchestrationId,
  };

  if (hasSelectedMembership) {
    result.useConsentServiceV3 = true;
  }

  if (campaignCode) {
    result.campaignCode = campaignCode;
  }

  return result;
};
